import { useInviteToken } from '@/components/auth/useInviteToken';
import { useGetContractSigningStateQuery } from '@/services/contractSigning';
import { Themes } from '@palmetto/dsm-tokens';
import { Spinner } from '@palmetto/palmetto-components';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';

import { Locale } from 'types';

import { EnglishOnlyNoticeStep, KeyTermsStep } from './AcknowledgementSteps';
import { ConsentStep } from './ConsentStep';
import { Copy, displayLocalesFor, inlineCopy } from './ContractSigningCopy';
import { isLinkGone } from './contractSigningErrors';
import { LanguageStep } from './LanguageStep';
import { ReviewAndSignStep } from './ReviewAndSignStep';

const BOTH = [Locale.en, Locale.es];
const LOGO_STYLE = { height: '44px', width: 'auto' };

const Frame = ({ title, children }: { title: string; children: ReactNode }) => (
  <div data-theme={Themes.core}>
    <Helmet>
      <title>{title}</title>
    </Helmet>
    <div className="flex min-h-screen flex-col bg-canvas-default">
      <div className="flex justify-center pt-6">
        <img src="/palmetto-brand-logo.svg" alt="Palmetto LightReach" style={LOGO_STYLE} />
      </div>
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col gap-8 px-4 py-8">{children}</main>
    </div>
  </div>
);

/**
 * Public, login-less landing page every customer completes before they get a Conga signing link. Mobile-first
 * and webview-safe: no new tabs, no Intercom, and every step survives a reload because the server owns progress.
 */
export const ContractSigning = () => {
  const { inviteToken } = useInviteToken();
  const [wasOvertaken, setWasOvertaken] = useState(false);
  const { data, isLoading, isError, error, refetch } = useGetContractSigningStateQuery(
    { inviteToken: inviteToken ?? '' },
    { skip: !inviteToken },
  );

  const greetingVars = useMemo(() => ({ firstName: data?.firstName ?? '' }), [data?.firstName]);

  // Another signer moved shared state (the language) or the consent text changed: reload and say why.
  const handleConflict = useCallback(() => {
    setWasOvertaken(true);
    void refetch();
  }, [refetch]);

  const helmetTitle = inlineCopy(data ? displayLocalesFor(data.contractLanguage) : BOTH, 'helmetTitle');

  if (isLoading) {
    return (
      <Frame title={helmetTitle}>
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </Frame>
    );
  }

  if (!inviteToken || (isError && isLinkGone(error))) {
    return (
      <Frame title={helmetTitle}>
        <Copy copyKey="linkNotFound" locales={BOTH} variant="heading" />
      </Frame>
    );
  }

  if (isError || !data) {
    return (
      <Frame title={helmetTitle}>
        <Copy copyKey="loadError" locales={BOTH} variant="heading" />
      </Frame>
    );
  }

  const locales = displayLocalesFor(data.contractLanguage);

  const renderStep = () => {
    switch (data.step) {
      case 'language':
        return <LanguageStep state={data} inviteToken={inviteToken} onConflict={handleConflict} />;
      case 'englishOnlyNotice':
        return <EnglishOnlyNoticeStep inviteToken={inviteToken} locales={locales} />;
      case 'keyTerms':
        return (
          data.keyTerms && <KeyTermsStep inviteToken={inviteToken} locales={locales} keyTerms={data.keyTerms} />
        );
      case 'consent':
        return (
          data.consentDisclosures && (
            <ConsentStep
              inviteToken={inviteToken}
              locales={locales}
              disclosures={data.consentDisclosures}
              onConflict={handleConflict}
            />
          )
        );
      case 'complete':
        return (
          data.contractLanguage && (
            <ReviewAndSignStep
              inviteToken={inviteToken}
              locales={locales}
              contractLanguage={data.contractLanguage}
            />
          )
        );
    }
  };

  return (
    <Frame title={helmetTitle}>
      {data.firstName && <Copy copyKey="greeting" locales={locales} vars={greetingVars} />}
      {data.step === 'language' && !data.contractLanguage && <Copy copyKey="intro" locales={locales} />}
      {wasOvertaken && data.step === 'language' && data.contractLanguage && (
        <Copy copyKey="languageTakenNotice" locales={locales} variant="error" />
      )}
      {renderStep()}
    </Frame>
  );
};
